
import { useState, useEffect } from 'react';

const MovingProcessSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  const steps = [
    {
      id: 1,
      title: '線上諮詢',
      subtitle: '免費估價',
      description: '透過 LINE 或電話聯繫我們，提供搬家需求與物品清單，專業顧問為您免費估價',
      icon: 'ri-customer-service-2-line',
      color: 'teal',
      details: ['24小時線上回覆', '傳送照片更精準', '免費專業建議'],
      image: 'https://public.readdy.ai/ai/img_res/3f854c12bc954d679940f47848326ed8.jpg'
    },
    {
      id: 2,
      title: '到府估價',
      subtitle: '現場評估',
      description: '專業人員到府評估物品數量、搬運難度，提供最精準的報價與搬家建議',
      icon: 'ri-file-list-3-line',
      color: 'emerald',
      details: ['免費到府服務', '詳細物品清點', '透明報價說明'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20mover%20with%20clipboard%20inspecting%20furniture%20and%20boxes%20in%20living%20room%2C%20home%20assessment%20for%20moving%20service%2C%20organized%20evaluation%20process%2C%20bright%20residential%20interior%2C%20professional%20uniform%2C%20detailed%20inspection%20scene&width=400&height=300&seq=process002&orientation=landscape'
    },
    {
      id: 3,
      title: '專業打包',
      subtitle: '精緻包裝',
      description: '使用專業包裝材料，為您的家具、電器、易碎品進行全方位防護包裝',
      icon: 'ri-gift-line',
      color: 'teal',
      details: ['專業防護材料', '易碎品特殊處理', '分類標記整理'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20movers%20carefully%20wrapping%20furniture%20with%20protective%20blankets%20and%20bubble%20wrap%2C%20organized%20packing%20materials%20and%20cardboard%20boxes%2C%20clean%20bright%20room%2C%20meticulous%20packing%20process%2C%20professional%20moving%20supplies&width=400&height=300&seq=process003&orientation=landscape'
    },
    {
      id: 4,
      title: '安全搬運',
      subtitle: '專業運送',
      description: '經驗豐富的搬運團隊，使用專業工具與技術，確保物品安全搬運',
      icon: 'ri-truck-line',
      color: 'emerald',
      details: ['專業搬運團隊', '安全固定綁帶', '全程保險保障'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20moving%20team%20loading%20furniture%20into%20clean%20white%20moving%20truck%2C%20organized%20loading%20process%20with%20safety%20straps%2C%20teamwork%20lifting%20sofa%2C%20professional%20movers%20in%20uniform%2C%20bright%20daylight%2C%20efficient%20moving%20service&width=400&height=300&seq=process004&orientation=landscape'
    },
    {
      id: 5,
      title: '定位擺放',
      subtitle: '貼心服務',
      description: '依照您的需求，將家具物品定位擺放至指定位置，讓您輕鬆入住新家',
      icon: 'ri-home-heart-line',
      color: 'teal',
      details: ['家具定位擺放', '基本組裝服務', '環境簡易清潔'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20movers%20placing%20furniture%20in%20new%20apartment%2C%20organized%20room%20setup%20with%20sofa%20and%20table%20in%20position%2C%20satisfied%20customer%20watching%2C%20bright%20modern%20living%20room%2C%20final%20placement%20service%2C%20happy%20moving%20completion%20scene&width=400&height=300&seq=process005&orientation=landscape'
    },
    {
      id: 6,
      title: '完成驗收',
      subtitle: '滿意保證',
      description: '搬家完成後進行驗收確認，確保所有物品完好無損，讓您安心滿意',
      icon: 'ri-checkbox-circle-line',
      color: 'emerald',
      details: ['物品完整確認', '滿意度調查', '售後服務保障'],
      image: 'https://readdy.ai/api/search-image?query=Happy%20family%20in%20new%20home%20with%20moving%20boxes%2C%20customer%20signing%20completion%20document%20with%20mover%2C%20satisfied%20handshake%20moment%2C%20bright%20cheerful%20atmosphere%2C%20successful%20moving%20completion%2C%20professional%20service%20finish&width=400&height=300&seq=process006&orientation=landscape'
    }
  ];

  // 自動輪播動畫
  useEffect(() => {
    if (!isAnimating) return;
    
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isAnimating, steps.length]);

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 via-white to-teal-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 標題區域 */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <i className="ri-flow-chart text-lg"></i>
            <span>搬家流程說明</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            <strong>專業搬家服務流程</strong>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            從諮詢到完成，<strong>建鈞搬家</strong>提供一站式專業服務，讓您的搬家過程輕鬆無憂
          </p>
        </div>

        {/* 流程動畫展示區 */}
        <div className="relative">
          {/* 進度條 - 桌面版 */}
          <div className="hidden lg:block absolute top-32 left-0 right-0 h-1 bg-gray-200 rounded-full mx-16">
            <div 
              className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
            />
          </div>

          {/* 步驟圖標 - 桌面版 */}
          <div className="hidden lg:grid grid-cols-6 gap-4 mb-12 relative z-10">
            {steps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => {
                  setActiveStep(index);
                  setIsAnimating(false);
                }}
                onMouseEnter={() => setIsAnimating(false)}
                className={`flex flex-col items-center cursor-pointer group transition-all duration-300 ${
                  index <= activeStep ? 'opacity-100' : 'opacity-50'
                }`}
              >
                {/* 圖標圓圈 */}
                <div className={`relative w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${
                  index === activeStep 
                    ? 'bg-gradient-to-br from-teal-500 to-emerald-500 scale-125 shadow-lg shadow-teal-500/30' 
                    : index < activeStep
                      ? 'bg-teal-500'
                      : 'bg-gray-300'
                }`}>
                  <i className={`${step.icon} text-white text-2xl ${index === activeStep ? 'animate-pulse' : ''}`}></i>
                  
                  {/* 步驟數字 */}
                  <div className={`absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    index <= activeStep ? 'bg-emerald-500 text-white' : 'bg-gray-400 text-white'
                  }`}>
                    {step.id}
                  </div>
                </div>

                {/* 步驟標題 */}
                <div className={`mt-4 text-center transition-all duration-300 ${
                  index === activeStep ? 'transform scale-110' : ''
                }`}>
                  <div className={`font-bold text-sm ${
                    index === activeStep ? 'text-teal-600' : 'text-gray-700'
                  }`}>
                    {step.title}
                  </div>
                  <div className="text-xs text-gray-500">{step.subtitle}</div>
                </div>
              </button>
            ))}
          </div>

          {/* 當前步驟詳情卡片 */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* 左側圖片 */}
              <div className="relative h-64 lg:h-auto overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-600/20 to-emerald-600/20 z-10" />
                <img 
                  src={steps[activeStep].image}
                  alt={steps[activeStep].title}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
                
                {/* 步驟標籤 */}
                <div className="absolute top-4 left-4 z-20">
                  <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">{steps[activeStep].id}</span>
                    </div>
                    <span className="font-bold text-gray-900">{steps[activeStep].title}</span>
                  </div>
                </div>

                {/* 動畫指示器 */}
                <div className="absolute bottom-4 left-4 right-4 z-20 lg:hidden">
                  <div className="flex gap-2 justify-center">
                    {steps.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setActiveStep(index);
                          setIsAnimating(false);
                        }}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          index === activeStep 
                            ? 'w-8 bg-white' 
                            : 'w-2 bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* 右側內容 */}
              <div className="p-6 lg:p-10 flex flex-col justify-center">
                {/* 手機版步驟指示 */}
                <div className="lg:hidden flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center`}>
                    <i className={`${steps[activeStep].icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <div className="text-sm text-teal-600 font-semibold">步驟 {steps[activeStep].id}/6</div>
                    <div className="text-xl font-bold text-gray-900">{steps[activeStep].title}</div>
                  </div>
                </div>

                {/* 桌面版標題 */}
                <div className="hidden lg:block mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-teal-500/20`}>
                      <i className={`${steps[activeStep].icon} text-white text-2xl`}></i>
                    </div>
                    <div>
                      <div className="text-sm text-teal-600 font-semibold">步驟 {steps[activeStep].id}</div>
                      <h3 className="text-2xl font-bold text-gray-900">{steps[activeStep].title}</h3>
                    </div>
                  </div>
                </div>

                {/* 描述 */}
                <p className="text-gray-600 text-base lg:text-lg leading-relaxed mb-6">
                  {steps[activeStep].description}
                </p>

                {/* 特點列表 */}
                <div className="space-y-3 mb-8">
                  {steps[activeStep].details.map((detail, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-3 animate-fadeIn"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-teal-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700 font-medium">{detail}</span>
                    </div>
                  ))}
                </div>

                {/* 操作按鈕 */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <a 
                    href="https://line.me/ti/p/@285yavdh"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-full font-semibold hover:from-green-600 hover:to-green-700 transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-green-500/30 cursor-pointer"
                  >
                    <img 
                      src="https://static.readdy.ai/image/9ef47c451bf9aafd7a63eea67af7d95d/c33a3c3f2fa31df7dfad05bfabc09369.webp" 
                      alt="LINE" 
                      className="w-5 h-5"
                    />
                    <span>LINE 免費諮詢</span>
                  </a>
                  <a 
                    href="tel:0978900081"
                    className="flex-1 bg-white border-2 border-teal-500 text-teal-600 px-6 py-3 rounded-full font-semibold hover:bg-teal-50 transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <i className="ri-phone-line text-lg"></i>
                    <span>0978-900-081</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* 播放/暫停控制 */}
          <div className="flex justify-center mt-8 gap-4">
            <button
              onClick={() => setIsAnimating(!isAnimating)}
              className="flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-md hover:shadow-lg transition-all cursor-pointer border border-gray-200"
            >
              <i className={`${isAnimating ? 'ri-pause-line' : 'ri-play-line'} text-teal-600 text-lg`}></i>
              <span className="text-gray-700 font-medium">{isAnimating ? '暫停動畫' : '播放動畫'}</span>
            </button>
            
            {/* 左右切換按鈕 */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setActiveStep((prev) => (prev === 0 ? steps.length - 1 : prev - 1));
                  setIsAnimating(false);
                }}
                className="w-12 h-12 bg-white rounded-full shadow-md hover:shadow-lg transition-all flex items-center justify-center cursor-pointer border border-gray-200 hover:border-teal-300"
              >
                <i className="ri-arrow-left-s-line text-teal-600 text-2xl"></i>
              </button>
              <button
                onClick={() => {
                  setActiveStep((prev) => (prev === steps.length - 1 ? 0 : prev + 1));
                  setIsAnimating(false);
                }}
                className="w-12 h-12 bg-white rounded-full shadow-md hover:shadow-lg transition-all flex items-center justify-center cursor-pointer border border-gray-200 hover:border-teal-300"
              >
                <i className="ri-arrow-right-s-line text-teal-600 text-2xl"></i>
              </button>
            </div>
          </div>

          {/* 底部服務保證 */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100 text-center hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-shield-check-line text-teal-600 text-xl"></i>
              </div>
              <div className="font-bold text-gray-900 text-sm">全程保險</div>
              <div className="text-xs text-gray-500">物品安全有保障</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100 text-center hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-price-tag-3-line text-emerald-600 text-xl"></i>
              </div>
              <div className="font-bold text-gray-900 text-sm">透明報價</div>
              <div className="text-xs text-gray-500">絕無隱藏費用</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100 text-center hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-time-line text-teal-600 text-xl"></i>
              </div>
              <div className="font-bold text-gray-900 text-sm">準時服務</div>
              <div className="text-xs text-gray-500">絕不讓您久等</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100 text-center hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-customer-service-2-line text-emerald-600 text-xl"></i>
              </div>
              <div className="font-bold text-gray-900 text-sm">24小時服務</div>
              <div className="text-xs text-gray-500">隨時為您服務</div>
            </div>
          </div>
        </div>
      </div>

      {/* 動畫樣式 */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default MovingProcessSection;
